import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useEmployees } from "@/hooks/use-employees";
import { EmployeeTable, sortEmployees, type SortKey, type SortDir } from "@/components/employees/EmployeeTable";
import { NyAnsattDialog } from "@/components/employees/NyAnsattDialog";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { isEmployeeRole } from "@/lib/constants";
import { SrIcon } from "@/components/ui/SrIcon";

export default function EmployeesPage() {
  const navigate = useNavigate();
  const { data: employees, isLoading, error } = useEmployees();
  const { activeUser } = useImpersonation();
  const canManageEmployees =
    activeUser?.role === "admin" || activeUser?.role === "superadmin";
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" || key === "role" ? "asc" : "desc");
    }
  };

  if (isLoading) return (
    <div className="page-container animate-fade-in">
      <Card>
        <CardContent className="p-6 flex items-center justify-center h-32">
          <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground"  />
        </CardContent>
      </Card>
    </div>
  );

  if (error) return <div className="px-6 py-8"><p className="text-destructive">Feil: {(error as Error).message}</p></div>;

  // VIB-1167: Ansatte-siden viser kun faktiske ansatte (admin/user). Konto-
  // roller (superadmin/test, external/revisor) skjules helt og administreres
  // på Brukere-siden. Telling og KPI-strip reflekterer kun ansatte.
  const ansatte = (employees ?? []).filter((u) => isEmployeeRole(u.role));

  const filtered = ansatte.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || (u.role ?? "").toLowerCase().includes(q);
  });

  const sorted = sortEmployees(filtered, sortKey, sortDir);

  // #609 (D1) fjernet «Snitt timekost»-kortet; VIB-1167 filtrerer til ansatte.
  const totalCapacity = ansatte.reduce((s, e) => s + (e.monthly_capacity_hours ?? 0), 0);
  const totalBudgetedHours = ansatte.reduce((s, e) => s + e.total_budgeted_hours, 0);

  return (
    <div className="page-container animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <SrIcon name="users" className="w-6 h-6 text-muted-foreground"  />
          <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] text-foreground tracking-wide">
            Ansatte
          </h1>
          <Badge variant="secondary" className="text-xs ml-1">{ansatte.length}</Badge>
        </div>
        {canManageEmployees && <NyAnsattDialog />}
      </div>

      {/* KPI Cards */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border border-border/50 bg-card/50">
              <span className="text-xs text-muted-foreground">Ansatte</span>
              <p className="text-2xl font-bold text-foreground mt-1">{ansatte.length}</p>
              <p className="text-xs text-muted-foreground mt-1">Aktive medarbeidere</p>
            </div>
            <div className="p-4 rounded-lg border border-border/50 bg-card/50">
              <span className="text-xs text-muted-foreground">Total kapasitet</span>
              <p className="text-2xl font-bold text-foreground mt-1">{totalCapacity}t/mnd</p>
              <p className="text-xs text-muted-foreground mt-1">Samlet månedlig</p>
            </div>
            <div className="p-4 rounded-lg border border-border/50 bg-card/50">
              <span className="text-xs text-muted-foreground">Tildelte timer</span>
              <p className="text-2xl font-bold text-foreground mt-1">{totalBudgetedHours}t</p>
              <p className="text-xs text-muted-foreground mt-1">Fra budsjetter</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search + Table */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="font-display text-lg text-foreground tracking-wide flex-1">
              Oversikt
            </h3>
            <div className="relative max-w-xs">
              <SrIcon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"  />
              <input
                type="text"
                placeholder="Søk navn, e-post, rolle..."
                className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <EmployeeTable sorted={sorted} sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort} onRowClick={(u) => navigate(`/ansatte/${u.id}`)} />
        </CardContent>
      </Card>
    </div>
  );
}
